/**
 * Backend cold-start prewarm.
 *
 * WHY: our backend runs on APIM Consumption + Y1 (scale-to-zero) Functions.
 * The FIRST request after an idle period pays a ~60-70s gateway + Function
 * cold-start; warm it's ~80-150ms. Crucially, LOGIN itself goes STRAIGHT to
 * Supabase (Frankfurt) — it does NOT touch our APIM/Functions at all. So while
 * a user spends ~10-30s typing email + password + MFA, our gateway and
 * Functions stay COLD, and the user eats the full cold-start AFTER they think
 * they've finished signing in (the dashboard's first data call is what wakes it).
 *
 * FIX: the moment an UNAUTHENTICATED user lands on any auth screen, fire a
 * fire-and-forget request at the backend so the gateway + Functions spin up
 * DURING login, and are warm by the time the dashboard needs data.
 *
 * Endpoint: the anonymous `/health` liveness probe. On APIM it lives on the
 * ops-tier API (path `api`), so the reachable URL is `${base}/api/health` — NOT
 * `/api/v1/health` (that path matches no operation on the v1 API and APIM would
 * 404 it AT THE GATEWAY without ever forwarding to the Function App, defeating
 * the purpose of waking the FA). `/api/health` matches the real health
 * operation → APIM forwards to the FA → both the gateway and the Function App
 * warm up. See bicep/07-compute/modules/apim-operations.bicep.
 *
 * It's deliberately fire-and-forget: `no-cors` so a CORS-preflight quirk can't
 * throw, response ignored (a 200/401/404 is all FINE — the point is to WAKE the
 * gateway + FA, not to read data), and any error is swallowed so an offline /
 * blocked fetch never disturbs the auth page render.
 */

// Module-level latch: only ping once per page load, no matter how many auth
// routes mount (login → mfa-verify → etc. all mount the same AuthLayout, and
// StrictMode double-invokes effects in dev). Reset only on a full reload.
let warmed = false;

/**
 * Fire a single fire-and-forget warmup request at the backend health probe.
 * No-op when there's no configured API base (local `pnpm dev` uses relative
 * paths through the Vite proxy and needs no prewarm) or when already warmed.
 */
export function warmBackend(): void {
  if (warmed) return;
  const base = import.meta.env['VITE_API_BASE_URL'] as string | undefined;
  if (!base) return;
  warmed = true;
  try {
    void fetch(`${base}/api/health`, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
    }).catch(() => {
      // Swallow: prewarm is best-effort. A failed/blocked/offline ping must
      // never surface an error on the login page.
    });
  } catch {
    // Some environments throw synchronously (e.g. a strict CSP blocking the
    // origin). Best-effort — ignore.
  }
}

/**
 * Test-only: reset the once-per-load latch so a test can assert the ping fires.
 * Not exported through the barrel — imported directly by warmup.test.ts.
 */
export function __resetWarmedForTest(): void {
  warmed = false;
}
